/**
 * TrendsPage Component
 *
 * Main trends exploration page showing popular search queries.
 * Features:
 * - Filters by region, time, category, and domain
 * - Trend charts (Chart.js)
 * - Grid of trend cards
 * - Pagination
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    Area,
    AreaChart,
    CartesianGrid,
    XAxis,
    YAxis,
    Tooltip as RechartsTooltip,
    ResponsiveContainer,
} from 'recharts';
import {
    ChartContainer,
    ChartTooltip,
    ChartTooltipContent,
} from '../../components/ui/chart';
import {
    Clock,
    Filter,
    LayoutGrid,
    ChevronRight,
    TrendingUp,
    Search,
    Loader2,
    Link2,
    X,
} from 'lucide-react';

import { ApiServices } from '../../services/ApiServices';
import { REGIONS, WORLDWIDE } from '../../constants/regions';
import { TIME_RANGES } from '../../constants/timeRanges';
import { CATEGORIES } from '../../constants/categories';
import { DOMAINS } from '../../constants/domains';
import { useAuth } from '../../context/AuthContext';
import { getChartColors, getCSSVariableAsRGB, getCSSVariableAsRGBA, getContrastTextColor } from '../../utils/chartColors';

// shadcn components
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    SelectGroup,
    SelectLabel
} from '../../components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../../components/ui/dialog';
import { Alert, AlertDescription, AlertTitle } from '../../components/ui/alert';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';
import {
    Pagination,
    PaginationContent,
    PaginationItem,
    PaginationLink,
    PaginationNext,
    PaginationPrevious,
    PaginationEllipsis,
} from '../../components/ui/pagination';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { cn } from '../../lib/utils';
import GeoFlag from '../../components/GeoFlag';
import RegionCombobox from '../../components/RegionCombobox';

// Removed ChartJS registration as we migrated to Recharts

function relatedNewsLabel(n) {
    const mod10 = n % 10;
    const mod100 = n % 100;
    if (mod10 === 1 && mod100 !== 11) return `${n} связанная новость`;
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return `${n} связанные новости`;
    return `${n} связанных новостей`;
}

// Simplified region display helper (same regions as News)
const getRegionDisplayLabel = (value) => {
    if (value === 'all') return WORLDWIDE.name;
    for (const group of REGIONS) {
        const country = group.countries.find((c) => c.code === value);
        if (country) return country.name;
    }
    return WORLDWIDE.name;
};

const TREND_TITLE_MAX_LENGTH = 25;

function truncateTrendTitle(title) {
    if (!title || title.length <= TREND_TITLE_MAX_LENGTH) return title;
    return `${title.slice(0, TREND_TITLE_MAX_LENGTH)}…`;
}

const TRENDS_DEFAULT_GEO = 'all';
const TRENDS_DEFAULT_DATE = 'now 7-d';
const TRENDS_DEFAULT_CATEGORY = 'all';
const TRENDS_DEFAULT_DOMAIN = 'all';
const TRENDS_DEFAULT_PAGE = 1;

function parseTrendsSearchParams(sp) {
    const pageRaw = sp.get('page');
    let page = TRENDS_DEFAULT_PAGE;
    if (pageRaw !== null && pageRaw !== '') {
        const n = parseInt(pageRaw, 10);
        if (!Number.isNaN(n) && n >= 1) {
            page = n;
        }
    }
    const qRaw = sp.get('q');
    const q = qRaw === null || qRaw === undefined ? '' : qRaw.trim();
    return {
        geo: sp.get('geo') || TRENDS_DEFAULT_GEO,
        date: sp.get('date') || TRENDS_DEFAULT_DATE,
        category: sp.get('category') || TRENDS_DEFAULT_CATEGORY,
        domain: sp.get('domain') || TRENDS_DEFAULT_DOMAIN,
        page,
        onlyWithCorrelation: sp.get('correlated') === '1',
        q,
    };
}

function buildTrendsSearchParamsSnapshot(snapshot) {
    const next = new URLSearchParams();
    if (snapshot.geo !== TRENDS_DEFAULT_GEO) {
        next.set('geo', snapshot.geo);
    }
    if (snapshot.date !== TRENDS_DEFAULT_DATE) {
        next.set('date', snapshot.date);
    }
    if (snapshot.category !== TRENDS_DEFAULT_CATEGORY) {
        next.set('category', snapshot.category);
    }
    if (snapshot.domain !== TRENDS_DEFAULT_DOMAIN) {
        next.set('domain', snapshot.domain);
    }
    if (snapshot.page !== TRENDS_DEFAULT_PAGE) {
        next.set('page', String(snapshot.page));
    }
    if (snapshot.onlyWithCorrelation) {
        next.set('correlated', '1');
    }
    const qVal = snapshot.q === null || snapshot.q === undefined ? '' : String(snapshot.q).trim();
    if (qVal !== '') {
        next.set('q', qVal);
    }
    return next;
}

// Helper keys for caching
function getTrendsCacheKey(filters, onlyWithCorrelation = false) {
    const correlationSuffix = onlyWithCorrelation ? '-correlated' : '-all';
    return `trends-${filters.geo}-${filters.date}-${filters.category}-${filters.domain}${correlationSuffix}`;
}

function getCachedTrends(cacheKey) {
    try {
        const cached = sessionStorage.getItem(`trends_cache_${cacheKey}`);
        if (cached) {
            const parsedData = JSON.parse(cached);
            const now = new Date().getTime();
            if (now - parsedData.timestamp < 10 * 60 * 1000) { // 10 minutes cache
                return parsedData;
            }
        }
    } catch (e) {
        console.warn('Error retrieving from cache:', e);
    }
    return null;
}

function setCachedTrends(cacheKey, trends, totalCount) {
    try {
        const cacheData = {
            trends,
            totalCount,
            timestamp: new Date().getTime()
        };
        sessionStorage.setItem(`trends_cache_${cacheKey}`, JSON.stringify(cacheData));
    } catch (e) {
        console.warn('Error setting cache:', e);
    }
}

// Data processing
const enhance_trends_data_from_array = (trendsArray) => {
    if (!trendsArray || !Array.isArray(trendsArray)) {
        return [];
    }
    return trendsArray.map(trend => {
        const query = trend.query ?? trend.title ?? '';
        return {
            query,
            category: trend.state ? trend.state.toLowerCase() : (trend.category || 'pending'),
            domain: trend.domain || 'Other',
            geo: trend.geo,
            is_garbage: Boolean(trend.is_garbage),
            search_count: trend.search_count || (trend.extracted_value ? `${Math.floor(trend.extracted_value / 1000)}K+` : '10K+'),
            extracted_value: trend.extracted_value !== null && trend.extracted_value !== undefined ? trend.extracted_value : null,
            rank: trend.rank || 0,
            timestamp: trend.timestamp,
            correlated_news: Array.isArray(trend.correlated_news) ? trend.correlated_news : [],
            correlated_news_count: typeof trend.correlated_news_count === 'number' ? trend.correlated_news_count : (Array.isArray(trend.correlated_news) ? trend.correlated_news.length : 0),
        };
    });
};

// --- Component Start ---
const TrendsPage = () => {
    const { isAuthenticated } = useAuth();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const isDarkMode = document.documentElement.classList.contains('dark');

    const trendsQueryKey = searchParams.toString();
    const filters = useMemo(
        () => parseTrendsSearchParams(new URLSearchParams(trendsQueryKey)),
        [trendsQueryKey]
    );
    const onlyWithCorrelation = filters.onlyWithCorrelation;
    const currentPage = filters.page;

    // State
    const [allFetchedTrends, setAllFetchedTrends] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [totalItems, setTotalItems] = useState(0);
    const [serpApiFetchInProgress, setSerpApiFetchInProgress] = useState(false);
    const [classificationTaskId, setClassificationTaskId] = useState(null);

    // Settings Modal
    const [settingsOpen, setSettingsOpen] = useState(false);

    // Chart State
    const [topTrendsChartData, setTopTrendsChartData] = useState([]);
    const [chartConfig, setChartConfig] = useState({});
    const [chartLoading, setChartLoading] = useState(true);
    const [chartError, setChartError] = useState(null);

    const [sortConfig, setSortConfig] = useState({ key: 'rank', direction: 'ascending' });
    const itemsPerPage = 20;
    const fetchInProgressRef = useRef(false);
    const SEARCH_DEBOUNCE_MS = 280;
    const [searchDraft, setSearchDraft] = useState('');
    const searchDebounceRef = useRef(null);

    useEffect(() => {
        setSearchDraft(filters.q);
    }, [trendsQueryKey]);

    useEffect(() => () => {
        if (searchDebounceRef.current !== null) {
            clearTimeout(searchDebounceRef.current);
        }
    }, []);

    const applyTitleSearchToUrl = useCallback((value) => {
        const trimmed = value.trim();
        const current = parseTrendsSearchParams(searchParams);
        setSearchParams(
            buildTrendsSearchParamsSnapshot({
                ...current,
                q: trimmed,
                page: TRENDS_DEFAULT_PAGE,
            }),
            { replace: true }
        );
    }, [searchParams, setSearchParams]);

    const handleTitleSearchChange = useCallback((value) => {
        setSearchDraft(value);
        if (searchDebounceRef.current !== null) {
            clearTimeout(searchDebounceRef.current);
            searchDebounceRef.current = null;
        }
        if (value.trim() === '') {
            applyTitleSearchToUrl('');
            return;
        }
        searchDebounceRef.current = window.setTimeout(() => {
            searchDebounceRef.current = null;
            applyTitleSearchToUrl(value);
        }, SEARCH_DEBOUNCE_MS);
    }, [applyTitleSearchToUrl]);

    // Chart Options Memo
    // Simplified chart fetching for Recharts
    const fetchTopTrendsForChart = useCallback(async (topTrends, geo) => {
        if (!topTrends || topTrends.length === 0) {
            setChartLoading(false);
            return;
        }

        try {
            setChartLoading(true);
            setChartError(null);

            const top3Trends = topTrends.slice(0, 3);
            const geoParam = geo || (filters.geo === 'all' ? 'US' : filters.geo);
            const dateParam = 'now 7-d';

            const queries = top3Trends.map(trend => trend.query).filter(Boolean);

            if (queries.length === 0) {
                setChartLoading(false);
                return;
            }

            const batchResponse = await ApiServices.getTrendDetailsBatch(queries, {
                date: dateParam,
                geo: geoParam
            });

            if (!batchResponse || !batchResponse.results) {
                setChartLoading(false);
                return;
            }

            const chartLabels = ['7d ago', '6d ago', '5d ago', '4d ago', '3d ago', '2d ago', 'Yesterday', 'Now'];
            const formattedData = chartLabels.map((date, idx) => {
                const entry = { date };
                top3Trends.forEach((trend, tIdx) => {
                    const query = trend.query;
                    const trendResults = batchResponse.results[query];
                    if (trendResults?.daily_data) {
                        const values = trendResults.daily_data.slice(-8);
                        entry[`trend_${tIdx}`] = values[idx]?.value || 0;
                    } else {
                        entry[`trend_${tIdx}`] = 0;
                    }
                });
                return entry;
            });

            const config = {};
            top3Trends.forEach((trend, idx) => {
                config[`trend_${idx}`] = {
                    label: decodeURIComponent(trend.query),
                };
            });

            setTopTrendsChartData(formattedData);
            setChartConfig(config);
            setChartLoading(false);
        } catch (err) {
            console.error('Error fetching chart data:', err);
            setChartError(err);
            setChartLoading(false);
        }
    }, [filters.geo]);

    // --- Main Fetch Logic ---
    const fetchAllTrendsData = useCallback(async (forceRefresh = false) => {
        if (fetchInProgressRef.current) return;
        fetchInProgressRef.current = true;
        setLoading(true);
        setError(null);

        let hasFetchedData = false;

        try {
            const cacheKey = getTrendsCacheKey(filters, onlyWithCorrelation);
            const cachedData = forceRefresh ? null : getCachedTrends(cacheKey);

            const params = {
                geo: filters.geo === 'all' ? '' : filters.geo,
                date: filters.date,
                category: filters.category === 'all' ? null : filters.category,
                domain: filters.domain === 'all' ? null : filters.domain,
                ...(onlyWithCorrelation ? { only_with_correlation: true } : {}),
            };

            if (cachedData?.trends) {
                setAllFetchedTrends(cachedData.trends);
                setTotalItems(cachedData.totalCount);
                setLoading(false);
                hasFetchedData = true;
            } else {
                if (!hasFetchedData) {
                    try {
                        const result = await ApiServices.fetchAllTrends(params, 500);

                        if (result?.geo_has_no_data === true && filters.geo !== 'all') {
                            setSerpApiFetchInProgress(true);
                            setClassificationTaskId(null);
                            try {
                                const serpData = await ApiServices.getTrends({
                                    ...params,
                                    force_serpapi: true,
                                    page_size: 500
                                });
                                let apiTrends = serpData?.trends || serpData?.trending_searches || [];
                                if (Array.isArray(serpData)) apiTrends = serpData;
                                const processed = enhance_trends_data_from_array(apiTrends);
                                setAllFetchedTrends(processed);
                                setTotalItems(processed.length);
                                setCachedTrends(cacheKey, processed, processed.length);
                                hasFetchedData = true;
                                const taskId = serpData?.classification_task_id;
                                if (taskId) setClassificationTaskId(taskId);
                            } catch (serpErr) {
                                console.error('SerpAPI fetch error:', serpErr);
                                setError('Не удалось загрузить данные для выбранного региона');
                                setAllFetchedTrends([]);
                                setTotalItems(0);
                            } finally {
                                setSerpApiFetchInProgress(false);
                            }
                        } else if (result?.trends?.length > 0) {
                            const processed = enhance_trends_data_from_array(result.trends);
                            setAllFetchedTrends(processed);
                            setTotalItems(processed.length);
                            setCachedTrends(cacheKey, processed, processed.length);
                            hasFetchedData = true;
                        } else {
                            const apiTrends = result?.trends || result?.trending_searches || [];
                            if (apiTrends.length > 0) {
                                const processed = enhance_trends_data_from_array(apiTrends);
                                setAllFetchedTrends(processed);
                                setTotalItems(processed.length);
                                setCachedTrends(cacheKey, processed, processed.length);
                                hasFetchedData = true;
                            } else {
                                setAllFetchedTrends([]);
                                setTotalItems(0);
                            }
                        }
                    } catch (e) {
                        console.error('Fetch error:', e);
                        setAllFetchedTrends([]);
                    }
                }
                setLoading(false);
            }
        } catch (err) {
            console.error("Critical fetch error:", err);
            setError("Failed to load trends.");
            setAllFetchedTrends([]);
            setLoading(false);
        } finally {
            fetchInProgressRef.current = false;
        }
    }, [filters.geo, filters.date, filters.category, filters.domain, onlyWithCorrelation]);

    useEffect(() => {
        ApiServices.trackAuditEvent('page_view', 'trends');
    }, []);

    // Effects: refetch only when data-affecting filters change (NOT on page change)
    useEffect(() => {
        setClassificationTaskId(null);
        if (!fetchInProgressRef.current) {
            fetchAllTrendsData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters.geo, filters.date, filters.category, filters.domain, onlyWithCorrelation]);

    // Poll classification status when we have a task ID (after SerpAPI on-demand fetch)
    useEffect(() => {
        if (!classificationTaskId) return;

        const poll = async () => {
            try {
                const res = await ApiServices.getTrendClassificationStatus(classificationTaskId);
                const status = res?.status;
                if (status === 'completed') {
                    setClassificationTaskId(null);
                    await fetchAllTrendsData(true);
                } else if (status === 'error' || status === 'not_found') {
                    setClassificationTaskId(null);
                }
            } catch {
                setClassificationTaskId(null);
            }
        };

        const intervalId = setInterval(poll, 3000);
        poll();

        return () => clearInterval(intervalId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [classificationTaskId]);

    const handleFilterChange = useCallback((key, value) => {
        const current = parseTrendsSearchParams(searchParams);
        setSearchParams(
            buildTrendsSearchParamsSnapshot({ ...current, [key]: value, page: TRENDS_DEFAULT_PAGE }),
            { replace: true }
        );
    }, [searchParams, setSearchParams]);

    const handlePageChange = useCallback((page) => {
        const current = parseTrendsSearchParams(searchParams);
        setSearchParams(buildTrendsSearchParamsSnapshot({ ...current, page }), { replace: true });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [searchParams, setSearchParams]);

    // Derived State: exclude garbage (backend filter); "only with correlation" is applied by API via param
    const sortedTrends = useMemo(() => {
        if (!allFetchedTrends?.length) return [];
        const nonGarbage = allFetchedTrends.filter(t => !t.is_garbage);
        const items = [...nonGarbage];
        items.sort((a, b) => {
            let av = a[sortConfig.key];
            let bv = b[sortConfig.key];
            if (sortConfig.key === 'rank') {
                av = parseInt(a.rank || 0);
                bv = parseInt(b.rank || 0);
            }
            return sortConfig.direction === 'ascending' ? av - bv : bv - av;
        });
        return items;
    }, [allFetchedTrends, sortConfig]);

    const normalizedTitleQuery = filters.q.trim().toLowerCase();
    const filteredTrends = useMemo(() => {
        if (normalizedTitleQuery === '') {
            return sortedTrends;
        }
        return sortedTrends.filter((t) => {
            const qq = (t.query || '').toLowerCase();
            return qq.includes(normalizedTitleQuery);
        });
    }, [sortedTrends, normalizedTitleQuery]);

    const totalPages = Math.max(1, Math.ceil(filteredTrends.length / itemsPerPage));

    useEffect(() => {
        if (currentPage > totalPages) {
            const current = parseTrendsSearchParams(searchParams);
            setSearchParams(
                buildTrendsSearchParamsSnapshot({ ...current, page: totalPages }),
                { replace: true }
            );
        }
    }, [currentPage, totalPages, searchParams, setSearchParams]);

    const currentTrends = useMemo(() => {
        const start = (currentPage - 1) * itemsPerPage;
        return filteredTrends.slice(start, start + itemsPerPage);
    }, [filteredTrends, currentPage, itemsPerPage]);

    // Sync chart with top 3 from the same trends shown in the grid (Popular queries)
    useEffect(() => {
        if (filteredTrends.length >= 3) {
            const top3 = filteredTrends.slice(0, 3);
            fetchTopTrendsForChart(top3, filters.geo);
        } else {
            setChartLoading(false);
            setTopTrendsChartData([]);
            setChartConfig({});
        }
    }, [filteredTrends, filters.geo, fetchTopTrendsForChart]);

    // Render Helpers
    const formatTimestamp = (ts) => {
        if (!ts) return { short: 'Недавно', full: '' };
        try {
            const date = new Date(typeof ts === 'number' ? (ts > 1e12 ? ts : ts * 1000) : ts);
            if (isNaN(date.getTime())) return { short: 'Недавно', full: '' };

            const diff = (new Date() - date) / 1000;
            if (diff < 60) return { short: 'Только что', full: date.toLocaleString() };
            if (diff < 3600) return { short: `${Math.floor(diff / 60)} мин. назад`, full: date.toLocaleString() };
            if (diff < 86400) return { short: `${Math.floor(diff / 3600)} ч. назад`, full: date.toLocaleString() };
            return { short: `${Math.floor(diff / 86400)} дн. назад`, full: date.toLocaleDateString() };
        } catch {
            return { short: 'Недавно', full: '' };
        }
    };

    // --- Render ---
    return (
        <div className="container mx-auto px-4 py-8 max-w-[1600px]">

            {/* Settings Modal */}
            <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Настройки</DialogTitle>
                    </DialogHeader>
                    <div className="py-4">
                        <label className="text-sm font-medium mb-2 block">Временной диапазон</label>
                        <Select value={filters.date} onValueChange={(val) => {
                            const current = parseTrendsSearchParams(searchParams);
                            setSearchParams(buildTrendsSearchParamsSnapshot({ ...current, date: val }), { replace: true });
                            setSettingsOpen(false);
                        }}>
                            <SelectTrigger>
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {TIME_RANGES.map(t => (
                                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setSettingsOpen(false)}>Отмена</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Header Section - Filters Left, Chart Right */}
            <div className="flex flex-col-reverse lg:flex-row gap-8 mb-8">
                {/* Left: Filters as compact grid */}
                <div className="flex-1 space-y-4">
                    <div>
                        <h1 className="text-3xl font-bold tracking-tight mb-1">Обзор трендов</h1>
                        <p className="text-muted-foreground text-sm">
                            Узнайте, что ищут пользователи прямо сейчас.
                        </p>
                    </div>

                    {/* Filters Grid */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {/* Region Filter */}
                        <RegionCombobox
                            value={filters.geo}
                            onChange={(code) => handleFilterChange('geo', code)}
                            className="w-full"
                        />

                        {/* Time Period Filter */}
                        <Select value={filters.date} onValueChange={(v) => handleFilterChange('date', v)}>
                            <SelectTrigger className="w-full">
                                <div className="flex min-w-0 items-center gap-2 [&>span]:truncate">
                                    <Clock className="h-4 w-4 shrink-0 text-muted-foreground" />
                                    <SelectValue placeholder="Период" />
                                </div>
                            </SelectTrigger>
                            <SelectContent>
                                {TIME_RANGES.map(t => (
                                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>

                        {/* Category Filter */}
                        <Select value={filters.category} onValueChange={(v) => handleFilterChange('category', v)}>
                            <SelectTrigger className="w-full">
                                <div className="flex min-w-0 items-center gap-2 [&>span]:truncate">
                                    <Filter className="h-4 w-4 shrink-0 text-muted-foreground" />
                                    <SelectValue placeholder="Категория" />
                                </div>
                            </SelectTrigger>
                            <SelectContent>
                                {CATEGORIES.map(c => (
                                    <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>

                        {/* Domain Filter */}
                        <Select value={filters.domain} onValueChange={(v) => handleFilterChange('domain', v)}>
                            <SelectTrigger className="w-full">
                                <div className="flex min-w-0 items-center gap-2 [&>span]:truncate">
                                    <LayoutGrid className="h-4 w-4 shrink-0 text-muted-foreground" />
                                    <SelectValue placeholder="Сегмент" />
                                </div>
                            </SelectTrigger>
                            <SelectContent>
                                {DOMAINS.map(d => (
                                    <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>

                        {/* Only with correlated news */}
                        <div className="flex items-center gap-2">
                            <Switch
                                id="trends-only-correlated"
                                checked={onlyWithCorrelation}
                                onCheckedChange={(checked) => {
                                    const current = parseTrendsSearchParams(searchParams);
                                    setSearchParams(
                                        buildTrendsSearchParamsSnapshot({
                                            ...current,
                                            onlyWithCorrelation: checked,
                                            page: TRENDS_DEFAULT_PAGE,
                                        }),
                                        { replace: true }
                                    );
                                }}
                            />
                            <Label htmlFor="trends-only-correlated" className="text-sm cursor-pointer whitespace-nowrap flex items-center gap-1.5">
                                <Link2 className="h-4 w-4 text-muted-foreground" />
                                Только со связью с новостями
                            </Label>
                        </div>
                    </div>

                    <div className="relative w-full">
                        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                        <Input
                            type="search"
                            value={searchDraft}
                            onChange={(e) => handleTitleSearchChange(e.target.value)}
                            placeholder="Поиск по запросу…"
                            className="pl-9 pr-10"
                            aria-label="Поиск трендов по тексту запроса"
                        />
                        {searchDraft !== '' && (
                            <button
                                type="button"
                                onClick={() => handleTitleSearchChange('')}
                                className="absolute right-2 top-1/2 -translate-y-1/2 rounded-sm p-1 text-muted-foreground hover:text-foreground"
                                aria-label="Очистить поиск"
                            >
                                <X className="h-4 w-4" />
                            </button>
                        )}
                    </div>
                </div>

                {/* Right: Chart (50% width) */}
                <div className="w-full lg:w-1/2 h-[280px]">
                    <div className="h-full w-full rounded-2xl border bg-card/50 p-4 transition-all hover:shadow-lg backdrop-blur-sm">
                        {chartLoading ? (
                            <div className="h-full flex items-center justify-center">
                                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                            </div>
                        ) : topTrendsChartData?.length > 0 ? (
                            (() => {
                                const palette = getChartColors();
                                const configWithColors = Object.fromEntries(
                                    Object.entries(chartConfig).map(([k], i) => [k, { ...chartConfig[k], color: palette[i % palette.length] }])
                                );
                                return (
                                    <ChartContainer config={configWithColors} className="h-full w-full">
                                        <AreaChart data={topTrendsChartData}>
                                            <defs>
                                                {Object.entries(configWithColors).map(([key, { color }]) => (
                                                    <linearGradient key={key} id={`fade_${key}`} x1="0" y1="0" x2="0" y2="1">
                                                        <stop offset="5%" stopColor={color} stopOpacity={0.8} />
                                                        <stop offset="95%" stopColor={color} stopOpacity={0} />
                                                    </linearGradient>
                                                ))}
                                            </defs>
                                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="oklch(var(--muted-foreground))" strokeOpacity={0.4} />
                                            <XAxis
                                                dataKey="date"
                                                tickLine={false}
                                                axisLine={false}
                                                tick={{ fill: 'oklch(var(--muted-foreground))', fontSize: 10 }}
                                                tickMargin={8}
                                            />
                                            <YAxis hide />
                                            <ChartTooltip content={<ChartTooltipContent />} />
                                            {Object.entries(configWithColors).map(([key, { color, label }]) => (
                                                <Area
                                                    key={key}
                                                    type="monotone"
                                                    dataKey={key}
                                                    name={label || key}
                                                    stroke={color}
                                                    fillOpacity={1}
                                                    fill={`url(#fade_${key})`}
                                                    strokeWidth={2}
                                                />
                                            ))}
                                        </AreaChart>
                                    </ChartContainer>
                                );
                            })()
                        ) : (
                            <div className="h-full flex items-center justify-center text-muted-foreground text-sm text-center px-4">
                                Нет данных для графика из Google Trends
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* SerpAPI on-demand fetch message */}
            {serpApiFetchInProgress && (
                <Alert className="mb-6 border-blue-500/50 bg-blue-500/10 text-blue-700 dark:text-blue-400">
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    <AlertTitle>Загрузка данных</AlertTitle>
                    <AlertDescription>
                        Получаем тренды для региона {getRegionDisplayLabel(filters.geo)} из внешнего источника. Это может занять несколько секунд...
                    </AlertDescription>
                </Alert>
            )}

            {/* Classification in progress - will auto-refresh when done */}
            {classificationTaskId && !serpApiFetchInProgress && (
                <Alert className="mb-6 border-amber-500/50 bg-amber-500/10 text-amber-700 dark:text-amber-400">
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    <AlertTitle>Классификация трендов</AlertTitle>
                    <AlertDescription>
                        Классификация и расчёт корреляций выполняются. Список обновится автоматически.
                    </AlertDescription>
                </Alert>
            )}

            {/* Trends Grid */}
            <h2 className="text-2xl font-semibold mb-6 flex items-center gap-2">
                Популярные запросы
                {filters.geo !== 'all' && <Badge variant="outline" className="ml-2">{filters.geo}</Badge>}
            </h2>

            {loading ? (
                <div className="h-64 flex items-center justify-center">
                    <Loader2 className="h-12 w-12 animate-spin text-primary" />
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                    {currentTrends.length > 0 ? (
                        currentTrends.map((trend, idx) => {
                            // Custom grid logic removed for standard 4-4-4-4-4 layout
                            const gridClass = "";

                            const timeData = formatTimestamp(trend.timestamp);

                            // Colors based on index
                            const colorVars = ['--chart-1', '--chart-2', '--chart-3', '--muted', '--muted', '--muted', '--muted', '--muted'];
                            const bgVar = idx < colorVars.length ? colorVars[idx] : '--muted';

                            // Determine text contrast
                            const isTop = idx < 3;
                            const bgColor = `var(${bgVar})`;
                            const textColorClass = isTop ? "text-primary-foreground" : "text-foreground";
                            const mutedTextClass = isTop ? "text-primary-foreground/80" : "text-muted-foreground";

                            return (
                                <Card
                                    key={`${trend.query}-${idx}`}
                                    className={cn(
                                        "cursor-pointer transition-all hover:-translate-y-1 hover:shadow-lg overflow-hidden group",
                                        gridClass
                                    )}
                                    onClick={() => {
                                        const detailParams = new URLSearchParams();
                                        const geo = trend.geo || (filters.geo !== 'all' ? filters.geo : null);
                                        if (geo) detailParams.append('geo', geo);
                                        if (filters.date !== TRENDS_DEFAULT_DATE) detailParams.append('date', filters.date);
                                        if (trend.category && trend.category !== 'pending') detailParams.append('category', trend.category);
                                        if (trend.domain && trend.domain !== 'Other') detailParams.append('domain', trend.domain);
                                        const detailQs = detailParams.toString();
                                        navigate(
                                            {
                                                pathname: `/trend/${encodeURIComponent(trend.query)}`,
                                                search: detailQs ? `?${detailQs}` : '',
                                            },
                                            { state: { trendsListSearch: searchParams.toString() } }
                                        );
                                    }}
                                >
                                    <CardContent className="p-5 h-full flex flex-col">
                                        {/* Header */}
                                        <div className="flex items-start justify-between gap-3 mb-3">
                                            <TooltipProvider>
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <h3 className="font-bold text-lg leading-tight group-hover:text-primary transition-colors line-clamp-1 break-words min-w-0">
                                                            {truncateTrendTitle(trend.query)}
                                                        </h3>
                                                    </TooltipTrigger>
                                                    <TooltipContent side="top" className="max-w-[320px]">
                                                        {trend.query}
                                                    </TooltipContent>
                                                </Tooltip>
                                            </TooltipProvider>
                                            <Badge variant={idx < 3 ? "default" : "secondary"} className="shrink-0 font-bold">
                                                #{trend.rank || idx + 1}
                                            </Badge>
                                        </div>

                                        {/* Category / Correlations */}
                                        <div className="flex flex-wrap gap-2 mb-auto">
                                            <Badge variant="outline" className="text-xs font-normal">
                                                {trend.category || 'pending'}
                                            </Badge>
                                            {trend.domain && trend.domain !== 'Other' && (
                                                <Badge variant="secondary" className="text-xs font-normal">
                                                    {trend.domain}
                                                </Badge>
                                            )}
                                            {typeof trend.correlated_news_count === 'number' && trend.correlated_news_count > 0 && (
                                                <Badge variant="secondary" className="bg-sky-500/90 text-sky-50 text-xs font-normal">
                                                    {relatedNewsLabel(trend.correlated_news_count)}
                                                </Badge>
                                            )}
                                            {trend.geo && (
                                                <GeoFlag geo={trend.geo} />
                                            )}
                                        </div>

                                        {/* Footer Info */}
                                        <div className="flex items-center justify-between text-xs text-muted-foreground mt-3">
                                            <div className="flex items-center gap-1">
                                                <TrendingUp className="h-3.5 w-3.5 text-green-500" />
                                                <span className="font-medium text-foreground">
                                                    {trend.search_count}
                                                </span>
                                            </div>
                                            <div className="flex items-center gap-1">
                                                <Clock className="w-3 h-3" />
                                                <span>{timeData.short}</span>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            );
                        })
                    ) : (
                        <div className="col-span-full py-12 text-center text-muted-foreground">
                            {normalizedTitleQuery !== '' && sortedTrends.length > 0
                                ? 'Нет запросов, совпадающих с поиском. Попробуйте другую фразу.'
                                : 'Трендов не найдено по выбранным критериям.'}
                        </div>
                    )}
                </div>
            )}

            {/* Pagination */}
            {!loading && filteredTrends.length > 0 && (
                <Pagination className="justify-center">
                    <PaginationContent>
                        <PaginationItem>
                            <PaginationPrevious
                                onClick={() => currentPage > 1 && handlePageChange(currentPage - 1)}
                                className={currentPage <= 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
                            />
                        </PaginationItem>

                        {/* Simplified pagination for brevity */}
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                            const p = i + 1;
                            return (
                                <PaginationItem key={p}>
                                    <PaginationLink
                                        isActive={currentPage === p}
                                        onClick={() => handlePageChange(p)}
                                        className="cursor-pointer"
                                    >
                                        {p}
                                    </PaginationLink>
                                </PaginationItem>
                            );
                        })}
                        {totalPages > 5 && <PaginationItem><PaginationEllipsis /></PaginationItem>}

                        <PaginationItem>
                            <PaginationNext
                                onClick={() => currentPage < totalPages && handlePageChange(currentPage + 1)}
                                className={currentPage >= totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"}
                            />
                        </PaginationItem>
                    </PaginationContent>
                </Pagination>
            )}
        </div>
    );
};

export default TrendsPage;